// @erp/ui/turnstile — the human check on every public form.
//
// THE HALF THAT MATTERS IS NOT HERE. This renders a widget and collects a
// token; the token means nothing until the gateway trades it with Cloudflare
// (@erp/platform/turnstile). Everything in this file is convenience for the
// person filling in the form — a page that skipped it would simply be refused.
//
// ── WHY A FACTORY AND NOT A COMPONENT ──
//
// Every form needs three things from the widget and they cannot travel through
// one JSX element: the widget itself, the token for its fetch headers, and a
// "solved yet?" flag for its submit button. `createTurnstile()` hands back all
// three, so a form reads:
//
//   const captcha = createTurnstile();
//   …
//   <captcha.Widget />
//   <Button disabled={!captcha.ready()}>Sign in</Button>
//   …
//   fetch(url, { headers: { ...captcha.headers() } });  // then captcha.reset()
//
// ── THEME ──
//
// Read from `color-scheme` on the document element, which daisyUI declares
// inside each theme block (verified in the built CSS:
// `:root:has(input.theme-controller[value=shippraDark]:checked),
//  [data-theme=shippraDark]{color-scheme:dark;…}`). That makes it correct for
// BOTH ways a theme can be set here — the theme-controller checkbox and the
// `[data-theme]` attribute @erp/ui/theme-toggle writes on <html> — with no
// localStorage guess and no attribute sniffing. `theme: "auto"` was the easy
// alternative and is wrong: it follows the OS on every paint, while this
// product's toggle consults the OS only on a first visit and then obeys the
// stored choice.
//
// ── UNCONFIGURED ──
//
// With no site key the widget renders nothing and `ready()` is true, so a
// developer's machine is not blocked by a control it cannot satisfy. This is
// NOT a bypass: the client has no secret and can never be the thing that fails
// closed. The SERVER refuses every public write when a real deployment has no
// secret configured (@erp/platform/turnstile — `enforced`).
import {
  type Accessor,
  type JSX,
  Show,
  createContext,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from "solid-js";
import { CircleAlert } from "lucide-solid";

/**
 * The header the token rides in.
 *
 * Named after Turnstile's own auto-injected form field so the wire stays
 * recognisable; a header rather than a body field because every form here is a
 * JSON `fetch` and the gateway must not consume a body its handler will read.
 * MUST match TURNSTILE_HEADER in @erp/platform/turnstile.
 */
export const TURNSTILE_HEADER = "cf-turnstile-response";

/**
 * Explicit rendering with a named onload callback — the documented, race-free
 * pattern. `turnstile.ready()` is the alternative and cannot be called before
 * the global it lives on exists.
 */
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__erpTurnstileReady";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      action?: string;
      theme?: "light" | "dark" | "auto";
      size?: "normal" | "flexible" | "compact";
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: (code?: string) => void;
    },
  ) => string | undefined;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

type TurnstileWindow = typeof globalThis & {
  turnstile?: TurnstileApi;
  __erpTurnstileReady?: () => void;
};

let pending: Promise<TurnstileApi> | null = null;

/** Load the API once per document, however many widgets ask for it. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (pending) return pending;
  pending = new Promise<TurnstileApi>((resolve, reject) => {
    const w = window as TurnstileWindow;
    if (w.turnstile) {
      resolve(w.turnstile);
      return;
    }
    w.__erpTurnstileReady = () => {
      if (w.turnstile) resolve(w.turnstile);
      else reject(new Error("turnstile loaded without an api"));
    };
    const el = document.createElement("script");
    el.src = SCRIPT_URL;
    el.async = true;
    el.defer = true;
    // A blocked script is a REAL state on a school network, not an edge case.
    // It rejects, the widget says so, and the submit button stays disabled —
    // the person is told why rather than left pressing a button that 403s.
    el.onerror = () => reject(new Error("turnstile script blocked"));
    document.head.appendChild(el);
  });
  return pending;
}

// ── The site key ───────────────────────────────────────────────────────────

/**
 * Injected by the portal Vite preset (packages/config/vite/portal.ts). Declared
 * locally with a `typeof` guard — the same shape __DEV_LAN_HOST__ uses — so a
 * build that does not define it (the playground) compiles and simply renders no
 * widget, rather than throwing on an undeclared identifier.
 */
declare const __TURNSTILE_SITE_KEY__: string | undefined;

/** The site key this bundle was built with, or undefined when it was built without one. */
export const buildTimeSiteKey = (): string | undefined =>
  typeof __TURNSTILE_SITE_KEY__ === "string" && __TURNSTILE_SITE_KEY__
    ? __TURNSTILE_SITE_KEY__
    : undefined;

const SiteKeyContext = createContext<string | undefined>(undefined);

/**
 * Publishes the site key to every form under it. Mounted ONCE per portal, at
 * the root, beside SessionProvider.
 *
 * The site key is PUBLIC — it is in the HTML of every page that renders a
 * widget — so it is configuration, not a secret. It still comes from the portal
 * rather than a constant in this package: a package that hardcodes one
 * deployment's identifier is a package that cannot serve a second install.
 */
export function TurnstileProvider(props: { siteKey?: string; children: JSX.Element }): JSX.Element {
  // The value is passed through VERBATIM, empty string included: "" is an
  // explicit "this tree has no widget", which is a different statement from
  // "nobody mounted a provider" and only the catalog ever makes it.
  return <SiteKeyContext.Provider value={props.siteKey}>{props.children}</SiteKeyContext.Provider>;
}

/**
 * The site key in force here: an enclosing provider's, else the one this bundle
 * was built with.
 *
 * THE BUILD-TIME FALLBACK IS THE POINT. A provider every portal has to remember
 * to mount is a control with a "somebody forgot" failure mode, and the thing
 * they would forget is the human check on the sign-in form. Portals mount
 * nothing; the catalog overrides.
 */
export const useTurnstileSiteKey = (): string | undefined => {
  const provided = useContext(SiteKeyContext);
  return provided === undefined ? buildTimeSiteKey() : provided || undefined;
};

// ── The handle ─────────────────────────────────────────────────────────────

export interface TurnstileHandle {
  /** The widget. Place it directly above the form's submit control. */
  Widget: (props: { class?: string }) => JSX.Element;
  /** The solved token, or null. Prefer `headers()`. */
  token: Accessor<string | null>;
  /** True when the form may be submitted — solved, or nothing to solve. */
  ready: Accessor<boolean>;
  /** Spread into a fetch's headers. Empty when this deployment has no widget. */
  headers: () => Record<string, string>;
  /**
   * Spend the token.
   *
   * Call after EVERY submit, successful or not: a Turnstile token is
   * single-use, so the second press of a button would send a token the server
   * has already redeemed and be refused for a reason the person cannot act on.
   */
  reset: () => void;
}

export interface TurnstileOptions {
  /**
   * A label for this form in Turnstile's analytics ("login", "otp-request").
   * Not a security boundary — see the action note in @erp/platform/turnstile.
   */
  action?: string;
  size?: "normal" | "flexible" | "compact";
}

/**
 * CLOUDFLARE'S FIXED WIDGET HEIGHT — 65px for `normal` and `flexible`
 * (`compact` is 140px). Reserved before the iframe arrives so the submit button
 * does not jump down under the pointer as the page settles. Not a Spectrum
 * number and not ours to choose: it is the size of a third-party frame.
 */
const RESERVED_HEIGHT = { normal: "65px", flexible: "65px", compact: "140px" } as const;

/** The current theme, read from the CSS that is actually applied. */
const currentScheme = (): "light" | "dark" =>
  getComputedStyle(document.documentElement).colorScheme.includes("dark") ? "dark" : "light";

export function createTurnstile(options: TurnstileOptions = {}): TurnstileHandle {
  const siteKey = useTurnstileSiteKey();
  const [token, setToken] = createSignal<string | null>(null);
  const [failed, setFailed] = createSignal<string | null>(null);

  /**
   * The live widget, owned by the Widget INSTANCE rather than by the handle.
   *
   * A form that swaps branches (the OTP form's request step becoming its verify
   * step) unmounts one Widget and mounts another, and Solid does not promise
   * that the old branch's cleanup runs before the new branch's mount. With a
   * single handle-level id, that ordering would make the old cleanup remove the
   * NEW widget and leave a form nobody can submit. Each instance owns its own
   * cell and clears the pointer only if it still points at itself.
   */
  type Live = { api: TurnstileApi | undefined; id: string | undefined };
  let live: Live | null = null;

  const reset = () => {
    setToken(null);
    if (live?.api && live.id !== undefined) live.api.reset(live.id);
  };

  const Widget = (props: { class?: string }): JSX.Element => {
    // Nothing to render and nothing to wait for.
    if (!siteKey) return null;

    let host!: HTMLDivElement;
    const size = options.size ?? "flexible";
    const self: Live = { api: undefined, id: undefined };
    live = self;

    const draw = (theme: "light" | "dark") => {
      if (!self.api) return;
      if (self.id !== undefined) self.api.remove(self.id);
      setToken(null);
      self.id = self.api.render(host, {
        sitekey: siteKey,
        ...(options.action ? { action: options.action } : {}),
        theme,
        size,
        callback: (t) => {
          setFailed(null);
          setToken(t);
        },
        // A token times out after 300 seconds. Dropping it here means a form
        // left open over lunch cannot be submitted with a stale token and
        // refused — the widget re-solves and the button re-enables.
        "expired-callback": () => setToken(null),
        "error-callback": () => {
          setToken(null);
          setFailed("Verification could not run. Check your connection and reload the page.");
        },
      });
    };

    onMount(() => {
      let painted: "light" | "dark" | null = null;
      void loadTurnstile()
        .then((loaded) => {
          self.api = loaded;
          painted = currentScheme();
          draw(painted);
        })
        .catch(() => setFailed("Verification could not load. It may be blocked on this network."));

      // The theme is CSS-only — a checked `.theme-controller` input, no event
      // of its own — so the one moment it can change is that input changing.
      // Turnstile cannot be re-themed in place, so the widget is rebuilt, which
      // costs the solved token; the button disables until it re-solves, which
      // is a second and is honest.
      const onChange = (e: Event) => {
        const t = e.target as HTMLElement | null;
        if (!t?.classList?.contains("theme-controller")) return;
        const next = currentScheme();
        if (next === painted) return;
        painted = next;
        draw(next);
      };
      document.addEventListener("change", onChange, true);
      onCleanup(() => document.removeEventListener("change", onChange, true));
    });

    onCleanup(() => {
      if (live === self) live = null;
      if (self.api && self.id !== undefined) self.api.remove(self.id);
      self.id = undefined;
    });

    return (
      // The field rhythm of every other control in a form: a flex column at
      // gap-1.5 with the message underneath (Field's own structure). `min-w-0`
      // for the same reason it is on every control here — a grid or flex parent
      // must be allowed to shrink this below the widget's 300px preference
      // rather than scrolling a phone sideways.
      <div
        role="group"
        aria-label="Human verification"
        class={`flex min-w-0 flex-col gap-1.5 ${props.class ?? ""}`}
      >
        {/* No visible label, deliberately: the widget renders "Verify you are
            human" inside itself, and a label above it would be the same
            sentence twice. The group carries the accessible name instead. */}
        <div ref={host} class="w-full" style={{ "min-height": RESERVED_HEIGHT[size] }} />
        <Show when={failed()}>
          {/* Spectrum's negative help text, the same anatomy Field uses: alert
              icon at a 7px gap, 3px top offset, 12px in the negative-900 ink. */}
          <span class="flex items-start gap-[7px] text-xs leading-snug text-error">
            <CircleAlert size={16} class="mt-[3px] shrink-0" aria-hidden="true" />
            {failed()}
          </span>
        </Show>
      </div>
    );
  };

  return {
    Widget,
    token,
    // No site key ⇒ nothing to solve ⇒ never block the form. The server is what
    // refuses when a real deployment is unconfigured.
    ready: () => !siteKey || token() !== null,
    headers: (): Record<string, string> => {
      const t = token();
      return t === null ? {} : { [TURNSTILE_HEADER]: t };
    },
    reset,
  };
}
